/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { connect } from 'react-redux';
import { css, SerializedStyles } from '@emotion/react';
import * as R from 'ramda';
import { Column, Row } from '../../layout/flexbox';
import * as Buttons from '../../forms/elements/buttons';
import CharacterCount from '../../forms/elements/CharacterCount';
import { isNilOrEmpty, assoc } from '../../language/module';
import { IAppState } from '../../core/redux/IAppState';
import { getHotlistTypesAction } from './getEntitlementHotlistTypesAction';
import { Loader } from '../../forms/elements/loader/Loader';
import { ApiError } from '../../core/redux/api/apiError';
import { getError, getLoading } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { EntitlementHotlistType as HotlistType } from './entitlementHotlistType';
import { GuestDetails } from '../guestDetails';
import { fullName } from '../module';
import { COLUMN_SPACING, ROW_SPACING, ROW_SPACING_WIDE } from '../../forms/styles';
import LabeledData from '../../forms/elements/LabeledData';
import LabelGroup from '../../forms/elements/LabelGroup';
import ErrorView from '../../forms/ErrorView';
import { Dropdown } from '../../forms/elements/select/Dropdown';
import { SmallText, TinyText } from '../../styles/styles';
import { isActive, getActivePassesWithSeason } from '../entitlements/module';
import { isEntitlementHotlistedForResort } from '../hotlist/module';
import { QuickEntitlement, EntitlementHotlist } from '../entitlements/models';
import { Resort } from '../../resortDetails/resort';
import { addYears } from '../../language/time';
import ImmutableMap from '../../language/ImmutableMap';
import DateInput from '../../forms/elements/DateInput';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import { getResource } from '../../language/resources/getResource';
import { MAX_COMMENT_CHARS, MIN_COMMENT_CHARS } from './constants';
import { MIDDLE_GREY } from '../../styles/colors';
import { PhoneInput, defaultCountries, parseCountry, ParsedCountry } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Input } from '../../forms/elements/Input';
import { DEFAULT_CONFIG } from '../../auth/config/defaultConfig';


const HotlistModalForm = css`
  display: flex;
  flex-direction: column;
  ${COLUMN_SPACING};
  max-width: 744px;
  margin: auto;
`;

const HotlistModalLabelContainer = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  ${ROW_SPACING_WIDE}
`;

const HotlistModalComments = css`
  min-height: 200px;
  padding: 10px;
  ${SmallText};
  border-radius: 6px;
  resize: none;
`;

const HotlistModalCommentGuidance = css`
  ${TinyText};
  color: grey;
  padding-bottom: 10px;
  margin-top: -8px;
`
const HotlistModalDirectLine = css`
  ${TinyText};
  color: grey;
  padding-bottom: 5px;
  margin-top: -8px;
`
const HotlistModalPhoneContainer = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const HotlistModalLoader = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
`;


 
const ResortContactNumberInput = css`
  padding-top: 5px;
  padding-right: 5px;
  --react-international-phone-height: 45px;
  --react-international-phone-font-size: 14px;
  --react-international-phone-flag-width: 25px;
  --react-international-phone-flag-height: 25px;
  --react-international-phone-border-color: ${MIDDLE_GREY};
  .react-international-phone-dial-code-preview { width: 30px; }
  .react-international-phone-input { width: 165px; :focus { border: 2px solid; } }
`;

const PhoneExtensionInput = css`
  margin-top: 5px; 
  width: 80px; 
  font-size: 14px; 
  padding-right: 11px;
  padding-left: 11px;
  }  
`;

const PhoneExtensionTitle = css`
  width: 25px; 
  ${TinyText}; 
  text-align: right; 
  padding-bottom: 3px; 
  padding-right: 3px; 
  align-self: flex-end;
`;

type Props = {
  guestId: string;
  guest?: GuestDetails;

  heading?: string;
  hotlistName?: string;
  hotlistId?: number;
  cancelButtonTitle?: string;
  submitButtonTitle?: string;
  submitButtonText?: string;

  dtwPassType?: number;

  homeResort?: Resort;
  resorts?: ImmutableMap<Resort>;
  alterraResorts: string[];
  enableGlobalHotlist?: boolean;
  enableResortSpecificHotlist?: boolean;

  hotlistTypes?: HotlistType[];
  loadingHotlistTypes?: boolean;
  errorHotlistTypes?: ApiError;

  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>;
  allEntitlementTypes: ImmutableMap<EntitlementType>;
  phoneInputCountryList?: string[];

  loadingSubmit?: boolean;
  errorSubmit?: ApiError;

  rootCss?: SerializedStyles;

  resources: Record<string, any>;
  dateFormat?: string;

  getHotlistTypes?: () => void;
  validateHotlistSubmission?: (givenState: State) => string | undefined;
  onDismiss: () => void;
  onCancel?: () => void;
  onSubmitHandler?: (givenState: State) => void;
  onSubmitSuccess?: () => void;
};


export type State = {
  hotlistType: HotlistType | undefined;
  comments: string;
  error: string | undefined;
  isSubmitting: boolean;
  selectedEntitlement: QuickEntitlement | undefined;
  usingGlobalHotlist: boolean;
  hotlistEffectiveDate: Date;
  hotlistExpirationDate: Date;
  hotlistResorts: Resort[];
  selectedResort: Resort | undefined;
  noResortItemsMessage: string;
  homeResort?: Resort;
  phoneNumberValue?: string,
  phoneNumberCountry?: string,
  phoneNumberExtension?: string,
};


/**
 * HotlistModal: a component that renders the add/remove guest to/from hotlist modal for Guest Card. This modal lets
 * agents "hotlist" (blacklist) guests providing a reason and some comments.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => ({
    guest: state.guests.get(props.guestId),

    homeResort:                  R.path(['selectedResort'], state),
    resorts:                     (R.path(['allResorts'], state.resortDetails) ||
      ImmutableMap.empty()) as ImmutableMap<Resort>,
    enableGlobalHotlist:         R.path(['enableGlobalHotlist'], state.resortToolsConfig),
    enableResortSpecificHotlist: R.path(['enableResortSpecificHotlist'], state.resortToolsConfig),

    dtwPassType: state.resortToolsConfig.dtwCategoryId,

    hotlistTypes:        R.path(['entitlementHotlistTypes', 'types'], state) ,
    loadingHotlistTypes: R.path(['entitlementHotlistTypes', 'isLoading'], state),
    errorHotlistTypes:   R.path(['entitlementHotlistTypes', 'error'], state),

    entitlementHotlists: R.path(['entitlementHotlists'], state),

    loadingSubmit: !!getLoading(state, state.guests.get(props.guestId).masterGuestId,
        ACTION_TYPE.ADD_TO_HOTLIST_REQUEST,
      )
                     || !!getLoading(state, String(props.hotlistId),
        ACTION_TYPE.REMOVE_FROM_ENTITLEMENT_HOTLIST_REQUEST,
      ),
    errorSubmit:   getError(state, state.guests.get(props.guestId).masterGuestId, ACTION_TYPE.ADD_TO_HOTLIST_REQUEST)
                     || getError(state, String(props.hotlistId), ACTION_TYPE.REMOVE_FROM_ENTITLEMENT_HOTLIST_REQUEST),

    allEntitlementTypes: state.entitlementTypesDetails?.allEntitlementTypes,
    resources: state.locale,
    dateFormat: state.defaultDateFormat,
    phoneInputCountryList: state.resortToolsConfig.phoneInputCountryList?.length > 0 ? 
      state.resortToolsConfig.phoneInputCountryList : 
      DEFAULT_CONFIG.phoneInputCountryList
  }),
  (dispatch) => ({
    getHotlistTypes: () => dispatch(getHotlistTypesAction()),
  }),
)
(class HotlistModal extends React.PureComponent<Props, State> {
  public state: State = {
    hotlistType:           undefined,
    comments:              '',
    error:                 undefined,
    isSubmitting:          false,
    selectedEntitlement:   undefined,
    usingGlobalHotlist:    false,
    hotlistResorts:        [] as Resort[],
    selectedResort:        undefined,
    hotlistEffectiveDate:  new Date(),
    hotlistExpirationDate: addYears(new Date(), 1)!,
    noResortItemsMessage:  getResource(this.props.resources, "HOTLIST_MODAL_FAILED_TO_LOAD_RESORTS"),
    phoneNumberValue:      undefined,
    phoneNumberCountry:    undefined,
    phoneNumberExtension:  undefined,
  };

  private phoneCountries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return this.props.phoneInputCountryList?.includes(iso2);
  });

  public getHotlistableResorts = (currentEnt?: QuickEntitlement): Resort[] => {

    const hotlistResorts: Resort[] = [];
    const allResorts               = (this.props.resorts || ImmutableMap.empty) as ImmutableMap<Resort>;

    if (this.props.enableGlobalHotlist) {
      hotlistResorts.push(allResorts.get('ALL') as Resort);
    }

    if (this.props.enableResortSpecificHotlist && this.props.homeResort) {
      hotlistResorts.push(this.props.homeResort);
    }

    /*if (this.props.homeResort && this.props.alterraResorts.includes(this.props.homeResort.resortAbbreviation))
    {
      hotlistResorts.push({
        resortId: -1, 
        resortAbbreviation: 'AA', 
        resortName: 'All Alterra Resorts', 
        isHomeResort: false
      });
    }*/

    return hotlistResorts.filter((res) => res && currentEnt &&
     !isEntitlementHotlistedForResort(currentEnt, res, this.props.entitlementHotlists));
  };


  public updateResortStateForEntitlement = (curState: State, currentEnt?: QuickEntitlement): State => {

    const hotlistResorts = this.getHotlistableResorts(currentEnt);
    const selectedResort = hotlistResorts.length === 1 ? R.head(hotlistResorts) : undefined;

    // Call resort change handler to trigger side effects
    this.onChangeResort(selectedResort);

    return ({
      ...curState,
      hotlistResorts:       hotlistResorts,
      noResortItemsMessage: currentEnt ? getResource(this.props.resources, "HOTLIST_MODAL_FAILED_TO_LOAD_RESORTS") : getResource(this.props.resources, 'HOTLIST_MODAL_NO_PASS_SELECTED'),
    });

  };


  public componentDidMount = () => {

    const entitlements = this.props.guest
      && getActivePassesWithSeason(this.props.dtwPassType!, this.props.guest, this.props.allEntitlementTypes)
      || [];

    const oneActiveEntitlement = R.filter(isActive, entitlements || []).length === 1;
    const selectedEntitlement  = oneActiveEntitlement ? R.head(entitlements) : undefined;

    this.setState(
      prev => ({
        ...this.updateResortStateForEntitlement(prev, selectedEntitlement),
        selectedEntitlement: selectedEntitlement,
      }),
    );

    if (isNilOrEmpty(this.props.hotlistTypes) && !this.props.loadingHotlistTypes) {
      this.props.getHotlistTypes!();
    }
  };


  public componentDidUpdate(prevProps: Props): void {
    // Submit finished successfully
    if (prevProps.loadingSubmit && !this.props.loadingSubmit) {
      this.setState(prev => assoc('isSubmitting', false, prev));
      this.props.onSubmitSuccess && this.props.onSubmitSuccess();
    }

    // Submit error
    if (!prevProps.errorSubmit && this.props.errorSubmit) {
      this.setState(prev =>
        assoc('error', this.props.errorSubmit!.errorMsg, prev)
      );
    }

    // Hotlist types error
    if (!prevProps.errorHotlistTypes && this.props.errorHotlistTypes) {
      this.setState(prev =>
        assoc('error', this.props.errorHotlistTypes!.errorMsg, prev)
      );
    }
  }


  private handleChange = (value, propName) => {
    propName && this.setState(prev => assoc(propName, value, prev));
  };


  private onChangeEntitlement = (entitlement: QuickEntitlement) => {
    this.handleChange(entitlement, 'selectedEntitlement');
    // Update available hotlists in Resorts dropdown based on what hotlists entitlement are already present on entitlement
    this.setState(prev => this.updateResortStateForEntitlement(prev, entitlement));
  };


  private onChangeResort = (resort?: Resort) => {
    this.handleChange(resort, 'selectedResort');
    this.setState(prev => assoc(
      'usingGlobalHotlist', resort?.resortAbbreviation === 'ALL', prev),
    );
  };


  private onChangeHotlistReason = (reason: HotlistType) => this.setState(
    prev => assoc('hotlistType', reason, prev),
  );


  private onChangeEffectiveDate = (date: Date) => {
    this.handleChange(date, 'hotlistEffectiveDate');
  };


  private onChangeExpirationDate = (date: Date) => {
    this.handleChange(date, 'hotlistExpirationDate');
  };


  private onChangeComments = (event) => {
    const newValue = event?.target?.value || '';
    this.setState(prev => assoc('comments', newValue, prev));
  };


  private onChangePhoneNumber = (newValue: string, meta: { country: ParsedCountry, inputValue: string }) => {
    this.handleChange(meta.inputValue, 'phoneNumberValue');
    this.handleChange(meta.country.iso2, 'phoneNumberCountry');
  };


  private onChangePhoneExtension = (event) => {
    const newValue = event?.target?.value || '';
    const digitsOnlyValue = newValue.replace(/\D/g, '') || '';
    this.handleChange(digitsOnlyValue, 'phoneNumberExtension');
  }

  private onKeyDownPreventSubmit = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }


  private onSubmit = (event: React.FormEvent) => {
    event.preventDefault(); // Stop browser from adding query params to URL >.>

    const errorMsg = this.props.validateHotlistSubmission && this.props.validateHotlistSubmission!(this.state);

    this.setState(prev => assoc('error', errorMsg, prev));

    if (errorMsg) {
      return;
    }

    this.setState(prev => assoc('isSubmitting', true, prev));
    this.props.onSubmitHandler && this.props.onSubmitHandler(this.state || {});
  };


  private getDropdownItems = () => {
    const passes = getActivePassesWithSeason(
      this.props.dtwPassType!,
      this.props.guest!,
      this.props.allEntitlementTypes,
    ) || [];

    return R.filter(isActive, passes);
  };


  public getEntitlementKey   = (entitlement: QuickEntitlement) => entitlement.entitlementId;
  public getEntitlementValue = (entitlement: QuickEntitlement) => entitlement.entitlementId;
  public getEntitlementLabel = (entitlement: QuickEntitlement) => entitlement.name || '';

  /* If your items array contains one item with a `value` attribute of 0, it will never be pre-selected. Hence we use
   resort abbreviations instead of Ids here (note that the global resort has Id 0). */
  public getResortKey   = (resort: Resort) => resort.resortAbbreviation;
  public getResortValue = (resort: Resort) => resort.resortAbbreviation;
  public getResortLabel = (resort: Resort) => resort.resortName;


  public getTypeKey   = (reason: HotlistType) => reason.id;
  public getTypeValue = (reason: HotlistType) => reason.id;
  public getTypeLabel = (reason: HotlistType) => reason.description;


  public render = () => (
    <div css={this.props.rootCss}>
      <form
        css={HotlistModalForm}
      >
        {/* Guest Name */}
        <LabeledData label={getResource(this.props.resources, 'HOTLIST_MODAL_GUEST_LABEL')} data={fullName(this.props.guest)} />

        { // Entitlement Drop-down
          !this.props.hotlistId && <LabelGroup label={getResource(this.props.resources, 'HOTLIST_MODAL_PASS_LABEL')}>
            <Dropdown
              resources={this.props.resources}
              items={this.getDropdownItems()}
              selectedItem={this.state.selectedEntitlement}
              placeholderMessage={getResource(this.props.resources, 'HOTLIST_MODAL_PASS_PLACEHOLDER')}
              disabled={
                isNilOrEmpty(this.props.guest!.entitlements)
                || this.state.isSubmitting
              }
              getKey={this.getEntitlementKey}
              getValue={this.getEntitlementValue}
              getLabel={this.getEntitlementLabel}
              noItemsMessage={getResource(this.props.resources, 'HOTLIST_MODAL_FAILED_ENTITLEMENTS')}
              onChange={this.onChangeEntitlement}
              rootCss={css`width: 300px;`}
            />
          </LabelGroup>
        }

        { // Resort Drop-down
          !this.props.hotlistId && <LabelGroup label={getResource(this.props.resources, 'HOTLIST_MODAL_RESORT_LABEL')}>
            <Dropdown
              resources={this.props.resources}
              items={this.state.hotlistResorts}
              selectedItem={this.state.selectedResort}
              placeholderMessage={getResource(this.props.resources, 'HOTLIST_MODAL_RESORT_PLACEHOLDER')}
              disabled={
                isNilOrEmpty(this.state.hotlistResorts)
                || this.state.isSubmitting
              }
              getKey={this.getResortKey}
              getValue={this.getResortValue}
              getLabel={this.getResortLabel}
              noItemsMessage={this.state.noResortItemsMessage}
              onChange={this.onChangeResort}
              rootCss={css`width: 300px;`}
            />
          </LabelGroup>
        }

        { // Resort Contact Phone Number
          !this.props.hotlistId && <LabelGroup label={getResource(this.props.resources, "HOTLIST_MODAL_REPORTER_CONTACT_NUMBER")}>
            <span css={HotlistModalDirectLine}>{getResource(this.props.resources, "HOTLIST_MODAL_DIRECT_LINE")}</span>
            <div css={HotlistModalPhoneContainer}>
              <PhoneInput css={ResortContactNumberInput} 
                disableDialCodeAndPrefix={true}
                showDisabledDialCodeAndPrefix={true}
                disabled={this.state.isSubmitting}    
                placeholder={getResource(this.props.resources, "HOTLIST_MODAL_ENTER_PHONE_NUMBER")}
                countries={this.phoneCountries}
                value={this.state.phoneNumberValue}
                onChange={this.onChangePhoneNumber}/>
              <div css={PhoneExtensionTitle}>
                <span>{getResource(this.props.resources, "HOTLIST_MODAL_PHONE_EXTENSION")}</span>
              </div>
              <Input css={PhoneExtensionInput}
                id='phoneExt'
                type='text'
                value={this.state.phoneNumberExtension}
                maxLength={6}
                onChange={this.onChangePhoneExtension}
                onKeyDown={this.onKeyDownPreventSubmit}
                autoFocus={false}/>
            </div>
          </LabelGroup>
        } 

        {!this.props.hotlistId
          && <div
            css={HotlistModalLabelContainer}
          >
            { /* Effective (/start) date selector */}
            <LabelGroup
              label={getResource(this.props.resources, 'HOTLIST_MODAL_FROM_LABEL')}
              rootCss={css`width: auto;`}
              subLabel={`(${this.props.dateFormat?.toUpperCase()})`}
              subLabelCss={css`
                font-weight: normal;
                ${TinyText}
              `}
            >
              <DateInput
                date={this.state.hotlistEffectiveDate}
                disabled={this.state.isSubmitting}
                onChange={this.onChangeEffectiveDate}
                placeholderText={getResource(this.props.resources, 'HOTLIST_MODAL_STARTING_DATE_PLACEHOLDER')}
                tooltipText={getResource(this.props.resources, 'HOTLIST_MODAL_STARTING_DATE_TOOLTIP')}
                dateFormat={this.props.dateFormat}
                rootCss={css`width: 9.4rem;`}
              />
            </LabelGroup>

            { /* Expiration (/end) date selector */}
            <LabelGroup
              label={getResource(this.props.resources, 'HOTLIST_MODAL_TO_LABEL')}
              rootCss={css`width: auto;`}
              subLabel={`(${this.props.dateFormat?.toUpperCase()})`}
              subLabelCss={css`
                font-weight: normal;
                ${TinyText}
              `}
            >
              <DateInput
                date={this.state.hotlistExpirationDate}
                disabled={this.state.isSubmitting}
                onChange={this.onChangeExpirationDate}
                placeholderText={getResource(this.props.resources, 'HOTLIST_MODAL_ENDING_DATE_PLACEHOLDER')}
                tooltipText={getResource(this.props.resources, 'HOTLIST_MODAL_ENDING_DATE_TOOLTIP')}
                dateFormat={this.props.dateFormat}
                rootCss={css`width: 9.4rem;`}
              />
            </LabelGroup>
          </div>
        }

        { // Reason Drop-down
          !this.props.hotlistId && <LabelGroup label={getResource(this.props.resources, 'HOTLIST_MODAL_REASON')}>
            <Dropdown
              resources={this.props.resources}
              items={this.props.hotlistTypes || []}
              selectedItem={this.state.hotlistType}
              placeholderMessage={getResource(this.props.resources, 'HOTLIST_MODAL_REASON_PLACEHOLDER')}
              disabled={
                this.props.loadingHotlistTypes
                || !!this.props.errorHotlistTypes
                || isNilOrEmpty(this.props.hotlistTypes)
                || this.state.isSubmitting
              }
              getKey={this.getTypeKey}
              getValue={this.getTypeValue}
              getLabel={this.getTypeLabel}
              noItemsMessage={getResource(this.props.resources, 'HOTLIST_MODAL_REASON_FAILED')}
              onChange={this.onChangeHotlistReason}
              autoFocus={true}
              rootCss={css`width: 300px;`}
            />
          </LabelGroup>
        }

        { /* Hostlist type label */
          !R.isNil(this.props.hotlistName) &&
          <LabeledData label={getResource(this.props.resources, 'HOTLIST_MODAL_HOTLIST_LABEL')} data={this.props.hotlistName!} />
        }

        {/* Comments */}
        <LabelGroup label={getResource(this.props.resources, 'HOTLIST_MODAL_COMMENTS_LABEL')} subLabel={getResource(this.props.resources, 'HOTLIST_MODAL_CHARACTER_INFO', MIN_COMMENT_CHARS, MAX_COMMENT_CHARS)}>
          { !this.props.hotlistId &&  
              <span css={HotlistModalCommentGuidance}>{getResource(this.props.resources, "HOTLIST_MODAL_COMMENT_GUIDANCE")}</span>
          }
          <Column flexGrow={1}>
                <textarea
                  value={this.state.comments || ''}
                  onChange={this.onChangeComments}
                  disabled={this.state.isSubmitting}
                  autoFocus={!!this.props.hotlistId}
                  css={HotlistModalComments}
                />
            <CharacterCount
              resources={this.props.resources}
              valueToCount={this.state.comments}
              minRequired={MIN_COMMENT_CHARS}
              maxAllowed={MAX_COMMENT_CHARS}
              rootCss={css`margin-top: 5px;`}
            />
          </Column>
        </LabelGroup>

        {/* Validation Message */}
        <ErrorView error={this.state.error} />

        {/* Submit */}
        <Row alignItems='baseline' justifyContent='flex-end' rootCss={ROW_SPACING}>
          {
            this.props.onCancel && this.props.cancelButtonTitle &&
            <Buttons.BigSecondary
              type='button'
              onClick={this.props.onCancel}
              title={this.props.cancelButtonTitle}
            >{getResource(this.props.resources, 'HOTLIST_MODAL_CANCEL')}</Buttons.BigSecondary>
          }

          <Buttons.BigNeutral
            type='submit'
            onClick={this.onSubmit}
            disabled={this.state.isSubmitting}
            title={this.props.submitButtonTitle}
          >
            {this.props.submitButtonText}
          </Buttons.BigNeutral>
        </Row>
      </form>
      {
        this.state.isSubmitting && <Loader
          rootCss={HotlistModalLoader}
        />
      }
    </div>
  );
});
